import { CoreService } from '../tools/core.service';
import { getLocalPermissionModel, storeLocalPermissionModel } from '../tools/local-storage';
import * as Logger from '../../common/tools/logger';
import * as Cookies from 'js-cookie';
import {
  ConsentParameters,
  PermissionClient as PermissionClientInterface,
  PermissionClientHandlers,
  PermissionClientOptions,
  PermissionClientSettings
} from '../interfaces';
import { CONFIG_VERSION } from '../../config';
import { PermissionModel } from '../../interfaces';

const IDENTITY_COOKIE_IDENTIFIER = 'tp_id';

/**
 * Provides a integration library for the United Internet Permission Core Script / CMP (Consent Management Platform) handler
 */
export class PermissionClient implements PermissionClientInterface {

  public options: PermissionClientOptions = null;
  public DEFAULT_HANDLER: PermissionClientHandlers = {
    onShowLayer: /* istanbul ignore next */ () => {},
    onResizeLayer: /* istanbul ignore next */ () => {},
    getIdentity: () => {
      return this.getIdentity();
    },
    getModel: (identity) => {
      return this.getModel(identity);
    },
    onChange: storeLocalPermissionModel,
    onComplete: () => {
      this.destroy();
    }
  };
  public version = CONFIG_VERSION;
  private moduleName = `PermissionClientApp`;
  private coreService: CoreService;

  /**
   * @public
   * @async
   * @function init
   * @description Function to initialize the Permission Client Application
   * @param {PermissionClientOptions} options Optional options
   * @return {Promise<void>}
   */
  public async init(options?: PermissionClientOptions): Promise<void> {
    try {
      PermissionClient.validateSettings(options as PermissionClientSettings);

      this.options = {
        ...this.DEFAULT_HANDLER,
        ...options
      };

      if (!this.coreService) {
        this.coreService = new CoreService();
      }

      await this.coreService.init(this.options);

      Logger.debug(this.moduleName, `initialize`, `Module initialized.`);
    } catch (error) {
      if (this.options && typeof this.options.onComplete === 'function') {
        this.options.onComplete();
      }
      Logger.error(this.moduleName, `init`, error);
    }
  }

  /**
   * @public
   * @async
   * @function update
   * @description Function to update the consent parameters
   * @param {ConsentParameters} consentParameters
   * @return {Promise<void>}
   */
  public async update(consentParameters?: ConsentParameters): Promise<void> {
    try {
      if (!this.coreService) {
        return;
      }

      if (consentParameters) {
        this.options.consentParameters = {
          ...(this.options.consentParameters || {}),
          ...consentParameters
        };
      }

      await this.coreService.init(this.options);
    } catch (error) {
      /* istanbul ignore next else */
      if (this.options && typeof this.options.onComplete === 'function') {
        this.options.onComplete();
      }
      Logger.error(this.moduleName, `update`, error);
    }
  }

  /**
   * @private
   * @function getIdentity
   * @description Default handler to get users identity from cookie
   * @return {string}
   */
  private getIdentity(): string {
    return Cookies.get(IDENTITY_COOKIE_IDENTIFIER) || null;
  }

  /**
   * Default handler to get the local permission model
   */
  private getModel(identity?: string): PermissionModel {
    let customerIdentity = identity;
    if (!customerIdentity && typeof this.options.getIdentity === 'function') {
      customerIdentity = this.options.getIdentity();
    }

    const model = getLocalPermissionModel(customerIdentity);
    model.identity = customerIdentity;

    if (typeof this.options.getTCString === 'function') {
      model.tcString = this.options.getTCString();
    }
    return model;
  }

  /**
   * @private
   * @function destroy
   * @description Function to exit correctly
   * @return {void}
   */
  private destroy(): void {
    this.coreService.destroy();
    this.coreService = null;
    this.options = null;
    this.DEFAULT_HANDLER = null;
    delete this.coreService;
    delete this.options;
    delete this.DEFAULT_HANDLER;
  }

  /**
   * @private
   * @static
   * @function validateSettings
   * @description Function to validate the publishers settings
   * @param {PermissionClientSettings} settings Optional settings
   * @return {void}
   */
  private static validateSettings(settings: PermissionClientSettings): void {
    if (!settings) {
      throw new Error('Missing settings object!');
    }

    if (!settings.portal || typeof settings.portal !== 'string') {
      throw new Error(`Required setting 'portal' must have a string value!`);
    }

    if (!settings.category || typeof settings.category !== 'string') {
      throw new Error(`Required setting 'category' must have a string value!`);
    }

    if (!!settings.section && typeof settings.section !== 'string') {
      throw new Error(`Optional setting 'section' must have a string value!`);
    }

    if (!!settings.permissionGroup && typeof settings.permissionGroup !== 'string') {
      throw new Error(`Optional setting 'section' must have a string value!`);
    }
  }

}
